import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2Icon } from 'lucide-react';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { SubpageHero } from '../../components/sections/subpages/SubpageHero';
import { PatternDecor } from '../../components/ui/PatternDecor';
export function BecomeAPartner() {
  return <>
      <SubpageHero eyebrow="Shared responsibility for shared impact" title="Become a Partner" paragraphs={['We welcome partnerships with organizations, experts, and institutions that share a commitment to education and family resilience, ethical responsibility, and long-term community impact.', 'We partner with those who prioritize responsibility, sustainability, and measurable outcomes.']} primaryCta={{
      label: 'Apply to Partner',
      to: '/en/contact'
    }} secondaryCta={{
      label: 'Contact Us',
      to: '/en/contact'
    }} image="https://cdn.magicpatterns.com/uploads/8SGVS9rHr65THgxC24jS6h/Become-Partner-3.jpg" imageAlt="Two Muslim educators reading picture books with a group of smiling children in a sunlit garden" compact masked data-id="element-4745" data-mp="2rdctnztweg4j" />

      {/* Partnership opportunities */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-sage-50/60" data-id="element-4746" data-mp="34av2uczlpm4a">
        <PatternDecor side="right" y="top" size="lg" data-id="element-4747" data-mp="2zb8n9wwavtsb" />
        <Container data-id="element-4748" data-mp="2uhnvwnp7py23">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4749" data-mp="2uh4c11g04zlf">
            Partnership Opportunities
          </h2>
          <p className="mt-5 max-w-5xl text-ink-700 leading-relaxed italic" data-id="element-4750" data-mp="2kkt04ckbqbri">
            Our strategic vision aligns with global leaders in human capital development. We offer collaborative frameworks focused on sustainable growth and societal impact:
          </p>
          <ul className="mt-10 max-w-6xl space-y-7" data-id="element-4751" data-mp="2bkmnsu0d3htx">
            {[{
            title: 'Co-development of Educational Ecosystems',
            body: 'Joint design and scaling of innovative educational platforms that bridge traditional values with modern professional standards.'
          }, {
            title: 'Research & Methodology Collaboration',
            body: 'Advancing the science of integration and identity preservation through data-driven research and peer-reviewed educational models.'
          }, {
            title: 'Human Capital Empowerment',
            body: 'Implementing large-scale training programs that foster professional mobility, civic engagement, and long-term societal stability.'
          }, {
            title: 'Strategic Cultural & Civic Outreach',
            body: 'Developing global outreach initiatives that promote cultural literacy, preserve heritage, and strengthen community resilience.'
          }, {
            title: 'Innovative Funding & Endowment Models',
            body: 'Partnering on sustainable social investment (Waqf) structures to ensure perpetual impact and resource longevity.'
          }, {
            title: 'Regional Implementation & Advocacy',
            body: 'Leveraging our established infrastructure to scale proven educational interventions across diverse global demographics.'
          }].map(opportunity => <li key={opportunity.title} className="flex items-start gap-3 md:gap-4 text-ink-700" data-id="element-4752" data-mp="90olm6t9kek7">
                <CheckCircle2Icon size={16} className="mt-1 flex-shrink-0 text-forest-700" strokeWidth={1.8} data-id="element-4753" data-mp="i01lq8vhzqs9" />
                <div data-id="element-4754" data-mp="1ygeamgyiqr22">
                  <h3 className="font-display text-lg text-ink-900" data-id="element-4755" data-mp="1tf8fkchdyrpd">
                    {opportunity.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed" data-id="element-4756" data-mp="2oqgcmx38rues">
                    {opportunity.body}
                  </p>
                </div>
              </li>)}
          </ul>
        </Container>
      </section>

      {/* Who we work with */}
      <section className="py-16 md:py-24 bg-white/60" data-id="element-4757" data-mp="33mnvnu2wypsh">
        <Container data-id="element-4758" data-mp="33nffk7n5ez2x">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4759" data-mp="33nywbl12bhfd">
            Who we work with
          </h2>
          <div className="mt-10 grid md:grid-cols-2 gap-5" data-id="element-4760" data-mp="3cn2i4wrm6axk">
            {[{
            title: 'Foundations & NGOs',
            body: 'Organizations focused on education, family resilience, and community development.'
          }, {
            title: 'Educational institutions',
            body: 'Schools, universities, and research bodies that share our commitments.'
          }, {
            title: 'Experts & advisors',
            body: 'Professionals in education, child development, ethics, and Islamic studies.'
          }, {
            title: 'Community organizations',
            body: 'Groups serving Muslim families in non-Muslim countries with on-the-ground reach.'
          }].map((card, i) => <motion.div key={card.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06
          }} className={`rounded-2xl border border-ink-900/5 p-6 shadow-soft ${['bg-sage-100', 'bg-[#F7EDE9]', 'bg-sand-100', 'bg-[#EDF2F7]'][i]}`} data-id="element-4761" data-mp="15s0hzomwy348" data-mpi={i}>
                <h3 className="font-display text-lg text-ink-900" data-id="element-4762" data-mp="fgye1wjowb5c">
                  {card.title}
                </h3>
                <p className="mt-3 text-ink-700 text-sm leading-relaxed" data-id="element-4763" data-mp="jijt9ojp51ip">
                  {card.body}
                </p>
              </motion.div>)}
          </div>
        </Container>
      </section>

      {/* How to apply */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-[#EDF2F7]/60" data-id="element-4764" data-mp="anvkvpitr3po">
        <PatternDecor side="left" y="middle" size="md" data-id="element-4765" data-mp="3rwscw748qhyq" />
        <Container data-id="element-4766" data-mp="s2bug4bdscn">
          <h3 className="font-display text-lg text-ink-900" data-id="element-4767" data-mp="slhoygowqab">How to apply</h3>
          <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-4768" data-mp="3iy6xgku2fj26">
            Reach out with a brief description of your organization and the
            focus area you're interested in. We'll respond with next steps,
            including a partnership brief and an introductory call.
          </p>
        </Container>
      </section>

      {/* Ready to support CTA */}
      <section className="py-16 md:py-24 bg-white/60" data-id="element-4769" data-mp="1m2sl7vkglfcc">
        <Container data-id="element-4770" data-mp="1w9vbqxg4ls2l">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-4771" data-mp="1w9bqb6r9oljt">
            <div className="max-w-xl" data-id="element-4772" data-mp="1rbtr4831xkp0">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4773" data-mp="2515bj6hqqkfq">
                Ready to support?
              </h2>
              <p className="mt-4 text-ink-700" data-id="element-4774" data-mp="209vq6i38639n">
                Choose the most relevant option — donation, scholarships, or
                institutional partnership. We'll respond quickly with next
                steps.
              </p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-4775" data-mp="2nzzpwedalivd">
              <Link to="/en/contact" data-id="element-4776" data-mp="2nzgjnux08ix6">
                <Button variant="primary" data-id="element-4777" data-mp="2jyxoprztewqr">Donate</Button>
              </Link>
              <Link to="/en/contact" data-id="element-4778" data-mp="2jwjypxjzsaq2">
                <Button variant="secondary" data-id="element-4779" data-mp="tlp46erzv4tt">Contact Us</Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>;
}